import { useEffect, useState } from 'react';

import { hasAsset } from 'shared/lib/utils';
import { useAssets, useGetUserProgramsQuery } from 'shared/api/retail';
import {
  NYC_DOE_ORG_SOURCE_ID,
  useAdminPortalEntitlement,
  useUserOrgs,
} from 'shared/api/onboarding';
import { AuthService } from 'shared/lib/keycloak';
import { ERROR_MESSAGE } from 'shared/config/constants';
import { getAmpConfigFlags } from 'shared/config/amp-config';

export const useIsEntitled = (searchableAsset?: string) => {
  const teacherBk = AuthService.getUserBk();
  const { isEntitlementErrorDisabled } = getAmpConfigFlags();

  const [isAssetFound, setIsAssetFound] = useState(false);
  const { districts, isLoading: isOrgsLoading } = useUserOrgs();

  const {
    userAssets = [],
    isError: isUserAssetsError,
    isLoading: isUserAssetsLoading,
  } = useAssets();

  // fetch user programs so it will be cached
  const { isLoading: isUserProgramsLoading, isError: isUserProgramsError } =
    useGetUserProgramsQuery(teacherBk);

  const { hasAlternativeErrorMsg } = useAdminPortalEntitlement();

  const isLoading =
    isUserAssetsLoading || isUserProgramsLoading || isOrgsLoading;

  useEffect(() => {
    if (isLoading) return;

    if (searchableAsset) {
      setIsAssetFound(hasAsset(searchableAsset, userAssets));
      return;
    }

    if (
      isEntitlementErrorDisabled &&
      (isUserAssetsError || isUserProgramsError)
    ) {
      throw new Error(ERROR_MESSAGE.ERR_NOT_ENTITLED);
    }

    // !TODO remove this check and three errors related logic below after RFS 25 release
    // do not throw PROGRAMS_NOT_SETUP, PROGRAMS_SETUP or ERR_NOT_ENTITLED after RFS 25
    if (isEntitlementErrorDisabled) {
      return;
    }

    const isNycDoeOrg = districts.find(
      (org) => org.sourcedId === NYC_DOE_ORG_SOURCE_ID,
    );

    if (hasAlternativeErrorMsg && !userAssets.length) {
      if (isNycDoeOrg) {
        throw new Error(ERROR_MESSAGE.PROGRAMS_NOT_SETUP);
      } else {
        throw new Error(ERROR_MESSAGE.PROGRAMS_SETUP);
      }
    }

    if (!userAssets.length || isUserAssetsError || isUserProgramsError) {
      throw new Error(ERROR_MESSAGE.ERR_NOT_ENTITLED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoading,
    userAssets,
    isUserAssetsError,
    searchableAsset,
    hasAlternativeErrorMsg,
    isUserProgramsError,
  ]);

  return { isLoading, isAssetFound };
};
